/**
 * Homepage layout when `document.body` has `page` + `page--home-page`
 * (CMS `pageType` `homepage`, see `contentful/utils/page-type-body-slug.js`
 * and `components/contentful/page.jsx`).
 *
 * Class contract (stable hooks used by selectors below):
 *   - `component--*` from `Component` wrapper — e.g. container, hero-banner,
 *     split-heading, media-carousel, product-tiles, category-card-grid, icon-set
 *   - `component-container__background` — full-bleed section backgrounds
 *   - `#app-main` — page content root
 */

/** Press logo grid (mobile): horizontal gap between 3 columns — Figma ~48px. */
const ICON_SET_LOGOS_MOBILE_COLUMN_GAP_PX = 10;
const ICON_SET_LOGOS_DESKTOP_COLUMN_GAP_PX = 40;
/** 3-up row: two gaps between three cells → (100% - 2 * gap) / 3 */
const ICON_SET_LOGOS_MOBILE_COL_BASIS = `calc((100% - 2 * ${ICON_SET_LOGOS_MOBILE_COLUMN_GAP_PX}px) / 3)`;

export const homePageGlobalStyles = {
  'body.page--home-page': {
    '& .component--hero-banner .amb-video': {
      height: {base: '100%', xl: 'unset'},
    },
    '& #app-main': {
      '& > .component--hero-banner': {
        '& .chakra-heading': {
          letterSpacing: {base: '-0.4px', lg: '-0.6px'},
          lineHeight: {'base': '100%', lg: '120%'},
        },
        '& .hero-content-wrapper': {
          gap: {base: '18px', lg: '20px'},
          '& .hero-banner__subtitle.amb-markup': {
            'p, strong, b, ul, li': {
              fontSize: {base: '16px', lg: '18px'},
            },
          },
          '& .hero-content-wrapper-inner > .chakra-text': {
            whiteSpace: 'pre-line',
            fontSize: {base: '16px', lg: '18px'},
          },
        },
        '& .cta-wrapper': {
          width: '100%',
          '& .chakra-stack': {
            gap: {base: '12px', lg: '18px'},
            width: '100%',
          },
        },

        '& .chakra-link': {
          paddingX: {base: '48px', lg: '48px'},
          paddingY: {base: '16px', lg: '16px'},
          textAlign: {base: 'center', lg: 'center'},
          height: '54px',
          '&.link--filled-dark': {
            border: '0',
            color: '#32312F',
            '& p': {
              color: '#32312F',
              lineHeight: '120%',
            },
            '&:hover': {
              border: '0',
              color: 'white',
              '& p': {
                color: 'white',
              },
            },
          },
        },
        '& .chakra-link, & .chakra-link p': {
          fontSize: {base: '14px', lg: '14px'},
          fontWeight: 600,
          lineHeight: '120%',
          whiteSpace: 'nowrap',
          textDecoration: 'none',
          '&:hover, &:hover p': {
            textDecoration: 'none',
          },
        },
        '& .hero-content-wrapper-inner': {
          paddingLeft: {base: '20px', lg: '80px'},
          paddingRight: {base: '20px', lg: '80px'},
        },
        '& .component--action-links': {
          marginTop: {base: '6px', lg: '12px'},
        },
      },
      '& .hero-content-wrapper-inner': {
        '& .chakra-link': {
          paddingX: {base: '48px', lg: '48px'},
          paddingY: {base: '16px', lg: '16px'},
          height: '54px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        '& .chakra-link, & .chakra-link p': {
          fontSize: {base: '14px', lg: '14px'},
          fontWeight: 600,
          lineHeight: '120%',
          whiteSpace: 'nowrap',
          textDecoration: 'none',
          '&:hover, &:hover p': {
            textDecoration: 'none',
          },
        },
        '& .chakra-link.link--filled-dark': {
          border: '0',
          color: '#32312F',
          '& p': {
            color: '#32312F',
            lineHeight: '120%',
          },
          '&:hover': {
            border: '0',
            color: 'white',
            '& p': {
              color: 'white',
            },
          },
        },
      },
    },
    '& .component--container--full-width-no:has(.component--hero-banner)': {
        maxWidth: '1440px',
        margin: '0 auto',
        paddingX: {base: '8px', lg: '80px'},
        '& .component--hero-banner': {
          paddingTop: {base: '0', lg: '80px'},
          paddingBottom: {base: '0', lg: '0'},
          '& .hero-content-wrapper': {
            '& .chakra-heading': {
              lineHeight: {'base': '100%', lg: '120%'},
              letterSpacing: {base: '-0.4px', lg: '-0.6px'},
            },
            '& .hero-banner__subtitle.amb-markup': {
              'p, strong, b, ul, li': {
                fontSize: {base: '16px', lg: '18px'},
                lineHeight: '160%',
              },
            },
            '& .hero-content-wrapper-inner': {
              paddingLeft: {base: '20px', lg: '80px'},
              paddingRight: {base: '20px', lg: '80px'},
            },
            '& .hero-content-wrapper-inner > .chakra-text': {
              whiteSpace: 'pre-line',
              fontSize: {base: '16px', lg: '18px'},
            },
            '& .component--action-links': {
              marginTop: {base: '6px', lg: '12px'},
            },
            '& .cta-wrapper': {
              width: '100%',
              '& .chakra-stack': {
                gap: {base: '12px', lg: '18px'},
                width: '100%',
              },
              '& .chakra-link': {
                paddingX: {base: '48px', lg: '48px'},
                paddingY: {base: '16px', lg: '16px'},
                height: '54px',
              },
              '& .chakra-link.link--filled-dark': {
                border: '0',
                color: '#32312F',
                '& p': {
                  color: '#32312F',
                  lineHeight: '120%',
                },
                '&:hover': {
                  border: '0',
                  color: 'white',
                  '& p': {
                    color: 'white',
                  },
                },
              },
              '& .chakra-link, & .chakra-link p': {
                fontSize: {base: '14px', lg: '14px'},
                fontWeight: 600,
                lineHeight: '120%',
                textAlign: {base: 'center', lg: 'center'},
              },
              '& .chakra-link:hover, & .chakra-link:hover p': {
                textDecoration: 'none',
              }
            }
          },
        },
    },
    '& .component--split-heading': {
      flexDirection: {base: 'column', lg: 'row'},
      gap: {base: '16px', lg: '40px'},
      justifyContent: {base: 'flex-start', lg: 'flex-start'},
      paddingTop: {base: '0', lg: '0'},
      paddingBottom: {base: '40px', lg: '80px'},
      '& .split-heading__heading': {
        letterSpacing: {base: '-0.28px', lg: '-0.4px'},
        fontWeight: 500,
        lineHeight: 1.2,
      },
      '& .split-heading__left-region, & .split-heading__right-region': {
        flex: {base: '0 0 auto', lg: '1 1 0'},
        minWidth: 0,
        width: {base: '100%', lg: 'auto'},
        maxWidth: {base: '100%', lg: 'none'},
      },
      '& .split-heading__right-region': {
        paddingTop: {base: 0, lg: '8px'},
      },
      '& .split-heading__description': {
        fontSize: {base: '16px', lg: '18px'},
        lineHeight: '160%',
      }
    },
    '& .component--media-carousel': {
      '& .swiper-cf-016': {
        paddingBottom: {base: '40px', lg: '80px'},
        '& .swiper-wrapper': {
          height: {base: 'auto !important', lg: 'unset'},
          '& .component--video': {
            maxHeight: {base: '100%', lg: '495px'},
            minHeight: {base: '231px', lg: '100%'},
            '& .amb-video': {
              position: {base: 'absolute', lg: 'relative'},
              top: {base: '50%', lg: '0'},
              transform: {base: 'translateY(-50%)', lg: 'translateY(0)'},
            },
          },
        },
      },
      '&:has(.swiper-cf-022)': {
        paddingTop: {base: '0', lg: '0'},
        paddingBottom: {base: '24px', lg: '0'},
        paddingX: {base: '20px', lg: '0'},
        marginTop: {base: '0', lg: '-6px'},
        '& .swiper-cf-022': {
          maxWidth: '1280px',
          margin: '0 auto',
          paddingX: {base: '0', lg: '0'},
          paddingY: {base: '0', lg: '0'},
          // Homepage only: category label overlays image bottom (Contentful Image `description`).
          '& .component--image': {
            aspectRatio: '3/4',
            display: 'block',
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
          },
          '& .component--image .chakra-image, & .component--image img': {
            display: 'block',
            height: '100%',
            objectFit: 'cover',
            width: '100%',
          },
          '& .component--image::after': {
            background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.6) 100%)',
            bottom: 0,
            content: '""',
            height: '112px',
            left: 0,
            pointerEvents: 'none',
            position: 'absolute',
            right: 0,
            zIndex: 1,
          },
          '& .component--image .image__description': {
            bottom: {base: '14px', md: '22px'},
            color: 'whiteAlpha.900',
            fontFamily: 'body',
            fontSize: {base: '16px', md: '20px'},
            fontStyle: 'normal',
            fontWeight: 500,
            left: {base: '14px', md: '19px'},
            letterSpacing: 0,
            lineHeight: '120%',
            margin: 0,
            marginTop: 0,
            padding: 0,
            position: 'absolute',
            right: {base: '14px', md: '19px'},
            textAlign: 'left',
            textTransform: 'none',
            wordBreak: 'break-word',
            zIndex: 2,
          },
          '& .component--image .image__description .chakra-text, & .component--image .image__description p': {
            color: 'inherit',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            lineHeight: 'inherit',
            margin: 0,
            textAlign: 'inherit',
          },
        },
      },
    },
    '& .component--container:has(.component--split-heading)': {
      paddingTop: {base: '56px', lg: '120px'},
      paddingBottom: {base: '56px', lg: '120px'},

      '& .component--action-links': {
        '& .chakra-stack': {
          justifyContent: {base: 'center', lg: 'center'},
        },
        '& .chakra-link': {
          paddingX: {base: '48px', lg: '48px'},
          paddingY: {base: '16px', lg: '16px'},
          height: '54px',
        },
        '& .chakra-link, & .chakra-link p': {
          fontSize: {base: '14px', lg: '14px'},
          fontWeight: 600,
          lineHeight: '120%',
        },
        '& .chakra-link:hover, & .chakra-link:hover p': {
          textDecoration: 'none',
        }
      },
      '& .component--media-carousel:not(:has(+ .component--action-links)) .swiper-cf-016': {
        paddingBottom: {base: '24px', lg: '0'},
      },
    },
    '& .component--container:not(:has(.component--hero-banner))': {
      paddingTop: {base: '56px', lg: '120px'},
      paddingBottom: {base: '56px', lg: '120px'},
    },
    '& .icon-set__wrapper': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: {base: '64px', lg: '24px'},
      paddingTop: {base: '56px', lg: '80px'},
      paddingBottom: {base: '56px', lg: '120px'},
    },
    '& .icon-set__title': {
      display: 'block',
      width: '100%',
      maxWidth: '1280px',
      margin: 0,
      textAlign: 'left',
      color: 'brandColors.primaryDarkLinen',
      fontFamily: 'body',
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '120%',
      letterSpacing: '2.4px',
      textTransform: 'uppercase',
    },
    '& .icon-set--logos .icon-set__grid': {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: {base: 'wrap', lg: 'nowrap'},
      alignItems: 'center',
      alignContent: {base: 'center', lg: 'stretch'},
      justifyContent: {base: 'center', lg: 'space-between'},
      rowGap: {base: '62px', lg: '0'},
      columnGap: {
        base: `${ICON_SET_LOGOS_MOBILE_COLUMN_GAP_PX}px`,
        lg: `${ICON_SET_LOGOS_DESKTOP_COLUMN_GAP_PX}px`,
      },
      width: '100%',
      maxWidth: '1280px',
      margin: '0 auto',
    },
    '& .icon-set--logos .icon-set__item': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: {base: `0 0 ${ICON_SET_LOGOS_MOBILE_COL_BASIS}`, lg: '0 0 auto'},
      width: {base: ICON_SET_LOGOS_MOBILE_COL_BASIS, lg: 'auto'},
      flexBasis: {base: ICON_SET_LOGOS_MOBILE_COL_BASIS, lg: 'auto'},
      minWidth: {base: '0', lg: 'auto'},
      maxWidth: {base: '143px', lg: 'none'},
      overflow: 'hidden',
      gap: '0',
    },
    '& .icon-set--logos .icon-set__item .icon': {
      width: '100%',
      maxWidth: '100%',
      minWidth: '0',
      alignItems: 'center',
    },
    '& .icon-set--logos .icon-set__item .icon__link': {
      width: '100%',
      maxWidth: '100%',
      minWidth: '0',
    },
    '& .icon-set--logos .icon-set__item .icon__image': {
      width: {base: '100%', lg: 'auto'},
      maxWidth: '100%',
      height: 'auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& svg': {
        width: {base: '100%', lg: 'auto'},
        height: 'auto',
        maxWidth: '100%',
        maxHeight: {base: '49px', lg: '50px'},
        display: 'block',
      },
      '& img': {
        width: 'auto',
        height: 'auto',
        maxWidth: '100%',
        maxHeight: {base: '49px', lg: '50px'},
        objectFit: 'contain',
        display: 'block',
        margin: '0 auto',
      },
    },
    '& .icon-set--logos .icon-set__item .icon__title': {
      display: 'none',
    },

    // Global styles: use @media directly — `sx` only works on components, not in theme.global
    '@media screen and (max-width: 399px)': {
      '& #app-main > .component--hero-banner .cta-wrapper .chakra-stack:has(.chakra-link + .chakra-link), & .component--container--full-width-no:has(.component--hero-banner) .cta-wrapper:has(.chakra-link + .chakra-link) .chakra-stack': {
        flexDirection: 'row',
        alignItems: 'stretch',
      },
      '& #app-main > .component--hero-banner .cta-wrapper:has(.chakra-link + .chakra-link) .chakra-link, & .component--container--full-width-no:has(.component--hero-banner) .cta-wrapper:has(.chakra-link + .chakra-link) .chakra-link': {
        flex: '1 1 0',
        minWidth: 0,
        width: 'auto',
        padding: '16px 12px',
      },

      '& .component--container--full-width-no:has(.component--hero-banner) .hero-content-wrapper': {
        paddingLeft: '0',
        paddingRight: '0',
      },
    },
  },
};
